/**
 * Minimal async-data hook for the Data Hub tabs.
 *
 * ExecBridge has `@tanstack/react-query` as a dependency but mounts no
 * QueryClientProvider anywhere, so `useQuery` would throw. The adapter seam
 * already abstracts fetching, so a local hook is enough — and it keeps the
 * library from requiring a provider to be mounted around it.
 */
import { useEffect, useRef, useState } from "react";

export type AsyncState<T> = {
  data: T | null;
  loading: boolean;
  error: Error | null;
};

export function useAdapterData<T>(
  load: () => Promise<T>,
  deps: unknown[] = []
): AsyncState<T> {
  const [state, setState] = useState<AsyncState<T>>({
    data: null,
    loading: true,
    error: null,
  });
  const loadRef = useRef(load);
  loadRef.current = load;

  useEffect(() => {
    let cancelled = false;
    setState(s => ({ ...s, loading: true, error: null }));
    loadRef
      .current()
      .then(data => {
        if (!cancelled) setState({ data, loading: false, error: null });
      })
      .catch((error: Error) => {
        if (!cancelled) setState({ data: null, loading: false, error });
      });
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, deps);

  return state;
}
